import type {
  OnboardingDraft,
  OnboardingInstallType,
} from "@/features/onboarding/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  ArrowsClockwiseIcon,
  ChatCircleDotsIcon,
  ClockIcon,
  PhonePlusIcon,
  type Icon,
} from "@phosphor-icons/react";
import { OnboardingLayout } from "./layout";

const OPTIONS: { value: OnboardingInstallType; icon: Icon }[] = [
  { value: "reuse_number", icon: ArrowsClockwiseIcon },
  { value: "new_number", icon: PhonePlusIcon },
  { value: "web_only", icon: ChatCircleDotsIcon },
  { value: "skip", icon: ClockIcon },
];

/**
 * How the agent gets a phone line — the choice, and nothing else.
 *
 * Asked while the analysis runs, so it has to stay a single decision. The
 * number to port, the current operator, the preferred area code: all of that
 * belongs to the number request this opens, which is where the client is
 * walked through it document by document. Collecting it here would turn a
 * ten-second answer into a form, for information nobody acts on today.
 *
 * None of the four is needed to try the product either — the demo connector
 * works regardless — so this is a decision about the *future* line, not a gate
 * in front of the test call, and "decide later" is a first-class option.
 */
export function StepTelephony({
  draft,
  onChange,
  onNext,
  onBack,
  isSubmitting,
  analysisPending,
}: {
  draft: OnboardingDraft;
  onChange: (patch: OnboardingDraft) => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
  analysisPending?: boolean;
}) {
  const { t } = useTranslation("onboarding");
  const selected = draft.telephony?.install_type;

  return (
    <OnboardingLayout
      step="telephony"
      title={t("steps.telephony.title")}
      intro={t("steps.telephony.intro")}
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!selected}
      isSubmitting={isSubmitting}
      analysisPending={analysisPending}
    >
      <div className="space-y-2">
        {OPTIONS.map(({ value, icon: OptionIcon }) => (
          <button
            key={value}
            type="button"
            onClick={() => onChange({ telephony: { install_type: value } })}
            className={
              "flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors " +
              (selected === value
                ? "border-primary bg-primary/5"
                : "hover:bg-slate-500/5")
            }
          >
            <OptionIcon
              className={
                "mt-0.5 h-5 w-5 shrink-0 " +
                (selected === value ? "text-primary" : "text-muted-foreground")
              }
              weight="fill"
            />
            <span className="flex-1">
              <span className="block text-sm font-medium">
                {t(`steps.telephony.options.${value}.title`)}
              </span>
              <span className="mt-0.5 block text-sm text-muted-foreground">
                {t(`steps.telephony.options.${value}.description`)}
              </span>
            </span>
          </button>
        ))}
      </div>
    </OnboardingLayout>
  );
}
